import { cn } from '@localagent/ui';

/**
 * One pending image attachment on the draft. `status` is the upload's own
 * state: images upload ON ATTACH so a failure surfaces here with a retry
 * control, rather than aborting an otherwise-ready send.
 */
export interface ImageAttachmentChip {
  /** Client-side id, stable for the life of the chip. */
  id: string;
  /** Neutralized display name (filename, or "Pasted image N"). */
  label: string;
  /** Object URL of the local preview; revoked by the owning hook. */
  previewUrl: string;
  status: 'preparing' | 'uploading' | 'ready' | 'error';
  /** Set only when `status === 'error'` — the reason, shown on the chip. */
  error?: string;
  /** Set once uploaded; what `ChatSession.send` attaches. */
  imageId?: string;
  /**
   * The SERVER's answer for the stored copy — dimensions and byte length after
   * client preparation and server-side stripping. Rendered so a user who
   * attached an over-cap original can see what actually landed under the cap
   * (the client loops on bytes, not on edge length).
   */
  width?: number;
  height?: number;
  sizeBytes?: number;
}

export interface ImageAttachmentChipsProps {
  attachments: ImageAttachmentChip[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  /** Refusal text for a non-image drop/paste; absent when there is none. */
  refusal?: string | null;
  onDismissRefusal?: () => void;
}

const STATUS_LABEL: Record<ImageAttachmentChip['status'], string> = {
  preparing: 'Preparing…',
  uploading: 'Uploading…',
  ready: '',
  error: 'Upload failed',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImageAttachmentChips({
  attachments,
  onRemove,
  onRetry,
  refusal,
  onDismissRefusal,
}: ImageAttachmentChipsProps) {
  if (attachments.length === 0 && !refusal) return null;

  return (
    <div className="mb-2 space-y-1.5" data-testid="image-attachment-chips">
      {refusal && (
        <div
          data-testid="image-attachment-refusal"
          className="flex items-start justify-between gap-2 rounded-md bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive"
        >
          <span>{refusal}</span>
          {onDismissRefusal && (
            <button
              type="button"
              onClick={onDismissRefusal}
              aria-label="Dismiss attachment refusal"
              className="shrink-0 rounded hover:bg-destructive/20"
            >
              <XGlyph />
            </button>
          )}
        </div>
      )}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((chip) => (
            <div
              key={chip.id}
              data-testid="image-attachment-chip"
              data-chip-status={chip.status}
              className={cn(
                'flex max-w-[16rem] items-center gap-2 rounded-md bg-muted px-2 py-1.5',
                chip.status === 'error' && 'bg-destructive/10',
              )}
            >
              <img
                src={chip.previewUrl}
                alt={chip.label}
                className="h-10 w-10 shrink-0 rounded object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium" title={chip.label}>
                  {chip.label}
                </p>
                {chip.status !== 'ready' && (
                  <p
                    data-testid="image-attachment-chip-status"
                    className={cn(
                      'truncate text-[11px]',
                      chip.status === 'error' ? 'text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {chip.error ?? STATUS_LABEL[chip.status]}
                  </p>
                )}
                {chip.status === 'ready' &&
                  chip.width !== undefined &&
                  chip.height !== undefined &&
                  chip.sizeBytes !== undefined && (
                    <p
                      data-testid="image-attachment-chip-meta"
                      data-size-bytes={chip.sizeBytes}
                      className="truncate text-[11px] text-muted-foreground"
                    >
                      {chip.width}×{chip.height} · {formatBytes(chip.sizeBytes)}
                    </p>
                  )}
              </div>
              {chip.status === 'error' && (
                <button
                  type="button"
                  data-testid="image-attachment-retry"
                  onClick={() => onRetry(chip.id)}
                  className="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
                >
                  Retry
                </button>
              )}
              <button
                type="button"
                data-testid="image-attachment-remove"
                onClick={() => onRemove(chip.id)}
                aria-label={`Remove ${chip.label}`}
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <XGlyph />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function XGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
